import { supabase } from '../clients/supabaseClient';
import type { ProductInfo, PriceRow } from '../types';

// ── Types ──────────────────────────────────────────────────────────────────────

export type ProductInventoryItem = {
    id: string;
    name: string;
    sku: string;
    display_price?: number | null;
    display_price_id?: string | null;
    stock?: number | null;
};

// ── Product search ─────────────────────────────────────────────────────────────

export async function fetchProductsByStoreFromDb(storeId: string): Promise<ProductInventoryItem[]> {
    const { data, error } = await supabase
        .from('product_inventory_view')
        .select('id, name, sku, display_price, display_price_id, stock, store_id, store_name')
        .eq('is_active', true)
        .eq('store_id', storeId)
        .order('name')
        .limit(100);

    if (error) throw error;
    return (data ?? []) as ProductInventoryItem[];
}

export async function searchProductsByStoreFromDb(
    storeId: string,
    query: string
): Promise<ProductInventoryItem[]> {
    const clean = query.trim().replace(/\s+/g, ' ');
    if (!clean) return fetchProductsByStoreFromDb(storeId);

    const { data, error } = await supabase
        .from('product_inventory_view')
        .select('id, name, sku, display_price, display_price_id, stock, store_id, store_name')
        .eq('is_active', true)
        .eq('store_id', storeId)
        .textSearch('search_fts', clean, { type: 'websearch', config: 'simple' })
        .order('name')
        .limit(100);

    if (error) throw error;
    return (data ?? []) as ProductInventoryItem[];
}

// ── Product detail ─────────────────────────────────────────────────────────────

export async function fetchProductInfoFromDb(
    productId: string,
    storeId?: string | null
): Promise<ProductInfo | null> {
    const query = supabase
        .from('product_inventory_view')
        .select('id,name,sku,store_name,store_id,display_price,stock,image_url,is_active,description,specification,keywords,health')
        .eq('id', productId);

    if (storeId && storeId !== 'null') query.eq('store_id', storeId);

    const { data, error } = await query.maybeSingle();
    if (error) throw error;
    return (data as ProductInfo | null) ?? null;
}

export async function fetchProductPriceHistoryFromDb(
    productId: string,
    storeId: string
): Promise<PriceRow[]> {
    const { data, error } = await supabase
        .from('product_prices')
        .select('price,valid_from,valid_to')
        .eq('product_id', productId)
        .eq('store_id', storeId)
        .order('valid_from', { ascending: false });

    if (error) throw error;
    return (data as PriceRow[]) ?? [];
}